// src/components/hooks/useFetchEvents.js
import { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import { getActivities } from '../../services/calendarService';
import { activityOptions, typeMapping } from '../ActivityUtils/activityOptions';

/* ===================== Debug ===================== */
const DEBUG_CAL =
  typeof window !== 'undefined' && localStorage.getItem('DEBUG_CAL') === '1';
const DBG = DEBUG_CAL;
const log = (...args) => { if (DBG) console.log('[FETCH]', ...args); };

const smartParseNumber = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return isNaN(v) ? 0 : v;
  if (typeof v !== 'string') return 0;
  const s = v.trim();
  const hasDot = s.includes('.');
  const hasComma = s.includes(',');
  if (hasDot && hasComma) {
    const lastDot = s.lastIndexOf('.');
    const lastComma = s.lastIndexOf(',');
    if (lastComma > lastDot) return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;
    return parseFloat(s.replace(/,/g, '')) || 0;
  }
  if (hasComma && !hasDot) return parseFloat(s.replace(',', '.')) || 0;
  return parseFloat(s) || 0;
};

const toMinutes = (v) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'string' && v.includes(':')) {
    const [hh = 0, mm = 0, ss = 0] = v.split(':').map(Number);
    return (hh * 60) + mm + (ss / 60);
  }
  const seconds = smartParseNumber(v);
  return seconds > 0 ? moment.duration(seconds, 'seconds').asMinutes() : 0;
};

const firstNumber = (...vals) =>
  vals.map(v => smartParseNumber(v)).find(v => !isNaN(v) && v > 0) ?? 0;

const firstMinutes = (...vals) =>
  vals.map(toMinutes).find(n => !isNaN(n) && n > 0) ?? 0;

/* ===================== Hook principal (SOLO CALENDARIO) ===================== */
const useFetchEvents = (externalUserId = null, selectedWeek = null) => {
  const [events, setEvents] = useState([]);
  const ranOnceRef = useRef(false);

  // ✅ Acepta overrides opcionales (startOverride, endOverride)
  const fetchEvents = useCallback(async (startOverride, endOverride) => {
    const userId = externalUserId || localStorage.getItem('userId');
    if (!userId) { log('⛔ sin userId'); return; }

    try {
      const start = startOverride
        ? moment(startOverride).startOf('day').toISOString()
        : moment().subtract(2, 'months').startOf('month').toISOString();

      const end = endOverride
        ? moment(endOverride).endOf('day').toISOString()
        : moment().add(1, 'month').endOf('month').toISOString();

      log('▶️ getActivities range:', start, '→', end, 'userId=', userId);
      const raw = await getActivities(start, end, userId);
      log('✅ getActivities raw count:', Array.isArray(raw) ? raw.length : raw);

      // 🔧 FILTRO TOLERANTE de assignedTo
      const uid = String(userId);
      const normId = (v) => {
        if (!v) return null;
        if (typeof v === 'string' || typeof v === 'number') return String(v);
        return String(v._id || v.id || (v.toString ? v.toString() : ''));
      };
      const data = (Array.isArray(raw) ? raw : []).filter((act) => {
        const a = normId(act.assignedTo);
        const keep = !a || a === uid;
        if (!keep && DBG) log('🧹 filtrado (assignedTo!=uid):', act._id || act.id, 'assignedTo=', a, 'uid=', uid);
        return keep;
      });
      log('📦 after filter count:', data.length);

      // ===== Mapeo a eventos para FullCalendar =====
      const mappedEvents = data.map((actividad) => {
        const typeKey = (actividad.type || actividad.title || 'otro').trim().toLowerCase();
        const formattedType = typeMapping[typeKey] || typeKey || 'otro';
        const actividadInfo =
          activityOptions.find((a) => a.type.toLowerCase() === formattedType) ||
          activityOptions.find((a) => a.type.toLowerCase() === 'otro');

        const c = actividad?.completed || {};
        const m = actividad?.metrics || {};
        const fileId = c?.fileId || c?.fitFileId;

        const dist = firstNumber(
          c?.distance, c?.distancia, c?.total_distance,
          m?.distance, m?.distancia, m?.total_distance, m?.totalDistance
        );
        const tmin = firstMinutes(
          c?.duration, c?.duracion, c?.time,
          m?.duracion, m?.duration, m?.time,
          m?.totalTime, m?.total_time, m?.totalTimerTime, m?.total_timer_time, m?.elapsed_time, m?.elapsedTime
        );
        const kcal = firstNumber(c?.calories, c?.kcal, c?.calorias, m?.calorias, m?.kcal);
        const elev = firstNumber(c?.elevation, c?.totalAscent, m?.elevation, m?.totalAscent);
        const tss  = firstNumber(c?.tss, m?.tss);

        const hasCompletedBase = !!fileId || dist > 0 || tmin > 0 || kcal > 0 || elev > 0 || tss > 0;

        return {
          id: String(actividad._id || actividad.id),
          title: actividad.title || 'Otro',
          start: actividad.date ? new Date(actividad.date).toISOString() : new Date().toISOString(),
          end: actividad.end ? new Date(actividad.end).toISOString() : undefined,

          editable: true,
          startEditable: true,
          durationEditable: false,
          allDay: false,

          backgroundColor: hasCompletedBase ? '#c5f7d2' : actividadInfo.color,
          textColor: '#fff',
          extendedProps: {
            assignedTo: actividad.assignedTo,
            trainer: actividad.trainer,
            date: actividad.date,

            title: actividad.title || 'Otro',
            type: formattedType,
            icon: actividadInfo.icon,
            description: actividad.description,
            completed: c,
            planned: actividad.planned || {},
            metrics: m,
            fileId, dist, tmin, kcal, elev, tss,
          },
        };
      });

      if (DBG) console.log('📅 setEvents (count):', mappedEvents.length);
      setEvents(mappedEvents);
    } catch (error) {
      console.error('❌ Error al obtener eventos:', error);
    }
  }, [externalUserId]);

  useEffect(() => {
    if (ranOnceRef.current) return;
    ranOnceRef.current = true;
    fetchEvents();
  }, [fetchEvents]);

  return { events, setEvents, fetchEvents };
};

export default useFetchEvents;
